import { Box, Center, HStack, Spinner, Text, VStack } from "@chakra-ui/react";
import { t } from "@lingui/macro";
import type { BudgetOrderByColumnEnum } from "@src/__generated__/graphql";
import type { BudgetQuoteModel } from "@src/components/modules/budgets/quotes/budget-quote-model";
import {
  ACTIONS_COLUMN_ID,
  type BodyColumnDef,
  DataTable,
} from "@src/components/ui-kit";
import { Pagination } from "@src/components/ui-kit/table/pagination";
import { FiltersPanelRoot } from "@src/utils/components/filters/components/filters-panel";
import { FiltersPanelFilters } from "@src/utils/components/filters/components/filters-panel-filters";
import { useStore } from "@src/utils/hooks";
import { observer } from "mobx-react-lite";
import { useRouter } from "next/router";
import { Fragment, useEffect, useLayoutEffect, useMemo } from "react";
import { AccountManagerRenderer } from "../export/components";
import {
  ActionsRenderer,
  BrandRenderer,
  BudgetNoRenderer,
  ClientApprovalRenderer,
  DateRenderer,
  PriceRenderer,
  ProjectRenderer,
} from "./components";

type BudgetQuoteRow = BudgetQuoteModel & Record<string, unknown>;

export const QuotesListing = observer(function ExportListing() {
  const { budgetQuotesListingStore: store } = useStore();
  const { query } = useRouter();

  const columns = useMemo<
    BodyColumnDef<BudgetQuoteRow, BudgetOrderByColumnEnum>[]
  >(
    () => [
      {
        id: "number",
        meta: { label: t`Number` },
        header: t`Number`,
        cell: ({ row }) => <Text>{row.original.number ?? ""}</Text>,
      },
      {
        id: "brand",
        meta: { label: t`Brand` },
        header: t`Brand`,
        cell: ({ row }) => <BrandRenderer row={row.original.budgets[0]} />,
      },
      {
        id: "project",
        meta: { label: t`Project` },
        header: t`Project`,
        cell: ({ row }) => <ProjectRenderer row={row.original.budgets[0]} />,
      },
      {
        id: "account-manager",
        meta: { label: t`Account manager` },
        header: t`Account manager`,
        cell: ({ row }) => (
          <AccountManagerRenderer
            accountManager={row.original.budgets[0].account_manager}
          />
        ),
      },
      {
        id: "budget-number",
        meta: { label: t`Budget no.` },
        header: t`Budget no.`,
        cell: ({ row }) => <BudgetNoRenderer budgets={row.original.budgets} />,
      },
      {
        id: "price",
        meta: { label: t`Price` },
        header: t`Price`,
        cell: ({ row }) => (
          <PriceRenderer
            price={row.original.budgets.reduce(
              (acc, budget) => acc + (budget.price ?? 0),
              0,
            )}
          />
        ),
      },
      {
        id: "date",
        meta: { label: t`Date` },
        header: t`Date`,
        cell: ({ row }) =>
          row.original.settings.date && (
            <DateRenderer date={new Date(row.original.settings.date)} />
          ),
      },
      {
        id: "sent-to-client",
        meta: { label: t`Sent to client` },
        header: t`Sent to client`,
        cell: ({ row }) =>
          row.original.sent_to_client_date && (
            <DateRenderer date={new Date(row.original.sent_to_client_date)} />
          ),
      },
      {
        id: "client-approval",
        meta: { label: t`Client approval` },
        header: t`Client approval`,
        cell: ({ row }) => <ClientApprovalRenderer quote={row.original} />,
      },
      {
        id: ACTIONS_COLUMN_ID,
        meta: { label: t`Actions` },
        header: t`Actions`,
        size: 200,
        cell: ({ row }) => <ActionsRenderer quote={row.original} />,
      },
    ],
    [],
  );

  useLayoutEffect(() => {
    if (!query) return;

    store.where.fromURLSearchParam(query);
    store.orderBy.fromURLSearchParam(query);
    store.pagination.fromURLSearchParam(query);
  }, []);

  useEffect(() => {
    store.fetchBudgetQuotes();
    store.setFilterOptions();
  }, []);

  return (
    <Fragment>
      <DataTable>
        <DataTable.NavBar>
          <HStack justify="end" py="4">
            <FiltersPanelRoot filters={store.where}>
              <VStack alignItems="start" w="full">
                <HStack justify="flex-end" w="full">
                  <FiltersPanelFilters />
                </HStack>
              </VStack>
            </FiltersPanelRoot>
          </HStack>
        </DataTable.NavBar>
        <Box pos="relative" role="table">
          {store.fetching && (
            <Center
              pos="absolute"
              top="0"
              right="0"
              bottom="0"
              left="0"
              bg="blackAlpha.200"
              backdropBlur="3xl"
            >
              <Spinner />
            </Center>
          )}
          <DataTable.Body
            data={store.tableData as BudgetQuoteRow[]}
            columns={columns}
            tableKey={store.tableKey}
          />
        </Box>
        <Pagination store={store.pagination} />
      </DataTable>
      <div />
    </Fragment>
  );
});
